// The first internal action vertical. Reads never write; mutations only accept
// a task observed by this authenticated executor in the current chat turn.
// The RPC owns authorization, optimistic concurrency and durable receipts.
export interface MirrorTaskResponse { data: unknown; error: unknown }
export interface MirrorTaskQuery extends PromiseLike<MirrorTaskResponse> {
  eq(column: string, value: unknown): MirrorTaskQuery;
  ilike(column: string, value: string): MirrorTaskQuery;
  order(column: string, options: { ascending: boolean; nullsFirst?: boolean }): MirrorTaskQuery;
  range(start: number, end: number): MirrorTaskQuery;
  maybeSingle(): PromiseLike<MirrorTaskResponse>;
}
export interface MirrorTaskDatabase {
  from(table: string): { select(columns: string): MirrorTaskQuery };
  rpc(name: string, args: Record<string, unknown>): PromiseLike<MirrorTaskResponse>;
}

export interface MirrorTaskExecutorOptions {
  sb: MirrorTaskDatabase;
  userId: string;
  turnId: string;
  channel: string;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const LOCAL_SOURCES = new Set([null, 'manual', 'mirror', 'voice']);
const PATCH_FIELDS = ['title', 'priority', 'due_date', 'time_label', 'done'] as const;
const TASK_FIELDS = ['id', 'title', 'priority', 'due_date', 'time_label', 'done', 'source', 'external_id', 'completed_at', 'created_at'] as const;
export const MIRROR_TASK_SELECT_FIELDS = ['user_id', ...TASK_FIELDS].join(',');
const MAX_OFFSET = 10_000;

export const MIRROR_TASK_TOOLS = [
  {
    name: 'search_vyv_tasks',
    description: 'Read the user\'s actual VYV tasks, including provenance. Search before selecting an existing task. Pagination is partial; more results may exist. This does not read the original external task provider. Correct: "show my open tasks" -> search_vyv_tasks with status:open and report only the returned page. Wrong: claim you read every task or accessed Google Tasks from this local search.',
    input_schema: {
      type: 'object', additionalProperties: false,
      properties: {
        query: { type: 'string', maxLength: 200 },
        status: { type: 'string', enum: ['open', 'completed', 'all'] },
        limit: { type: 'integer', minimum: 1, maximum: 20 },
        cursor: { type: 'integer', minimum: 0, maximum: MAX_OFFSET },
      },
      required: [],
    },
  },
  {
    name: 'get_vyv_task',
    description: 'Read one actual VYV task by an observed ID. Never invent an ID. Includes its local or imported source; reading a local copy does not access its original external provider. Correct: use the exact ID returned by search_vyv_tasks to inspect the selected task. Wrong: invent an ID from the title or say its external provider was queried.',
    input_schema: { type: 'object', additionalProperties: false, properties: { id: { type: 'string', format: 'uuid' } }, required: ['id'] },
  },
  {
    name: 'update_vyv_task',
    description: 'Only on an explicit request in the current user message, update a LOCAL VYV task already read in this turn with search_vyv_tasks or get_vyv_task. Use its exact ID. Resolve ambiguous matches with the user. Allowed patch: title, priority (Critical/Important/Normal), due_date (civil date YYYY-MM-DD only; null clears), time_label (null clears), done (true completes, false reopens). Resolve relative days in the user\'s timezone, then send the calendar date; the executor preserves it as UTC midnight for the existing Tasks date contract. Do not invent a due time or send an offset timestamp. Imported tasks must use their external executor; never claim the external provider changed. Success requires the returned database receipt. Correct: user asks to complete a uniquely identified local task -> read it, patch done:true, confirm only after ok:true with database_readback. Correct date: move a task to October 5 -> due_date:"2026-10-05". Wrong: choose between two plausible tasks, update an imported copy, send due_date:"2026-10-05T00:00:00+02:00", or claim success before the receipt.',
    input_schema: {
      type: 'object', additionalProperties: false,
      properties: {
        id: { type: 'string', format: 'uuid' },
        patch: {
          type: 'object', additionalProperties: false, minProperties: 1,
          properties: {
            title: { type: 'string', minLength: 1, maxLength: 1000 },
            priority: { type: 'string', enum: ['Critical', 'Important', 'Normal'] },
            due_date: { type: ['string', 'null'], description: 'Civil calendar date YYYY-MM-DD, without a time or timezone; null clears.', pattern: '^\\d{4}-\\d{2}-\\d{2}$' },
            time_label: { type: ['string', 'null'], maxLength: 100 },
            done: { type: 'boolean' },
          },
        },
      },
      required: ['id', 'patch'],
    },
  },
] as const;

export function isMirrorTaskTool(name: string): boolean {
  return MIRROR_TASK_TOOLS.some(tool => tool.name === name);
}

function object(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : null;
}

function onlyKeys(input: Record<string, unknown>, keys: readonly string[]): boolean {
  return Object.keys(input).every(key => keys.includes(key));
}

function taskData(raw: unknown): Record<string, unknown> | null {
  const row = object(raw);
  if (!row || !TASK_FIELDS.every(key => Object.prototype.hasOwnProperty.call(row, key)) || typeof row.user_id !== 'string' || !UUID.test(row.user_id) || typeof row.id !== 'string' || !UUID.test(row.id) || typeof row.title !== 'string' || typeof row.priority !== 'string' || !['Critical', 'Important', 'Normal'].includes(row.priority) || typeof row.done !== 'boolean') return null;
  if (['source', 'external_id', 'time_label'].some(key => row[key] !== null && typeof row[key] !== 'string')) return null;
  if (['due_date', 'completed_at', 'created_at'].some(key => row[key] !== null && (typeof row[key] !== 'string' || !Number.isFinite(Date.parse(row[key] as string))))) return null;
  const out: Record<string, unknown> = { user_id: row.user_id };
  for (const key of TASK_FIELDS) out[key] = row[key];
  return out;
}

export function buildMirrorTaskSnapshot(raw: unknown, owner: string): Record<string, unknown> | null {
  return object(raw)?.user_id === owner ? taskData(raw) : null;
}

export function isLocalMirrorTaskSource(source: unknown): boolean {
  return LOCAL_SOURCES.has(source as string | null);
}

function validDate(value: unknown): boolean {
  if (value === null) return true;
  if (typeof value !== 'string') return false;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const parsed = new Date(value + 'T00:00:00Z');
    return Number(value.slice(0, 4)) > 0 && Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
  }
  return /^\d{4}-\d{2}-\d{2}T(?:[01]\d|2[0-3]):[0-5]\d:[0-5]\d(?:\.\d{1,6})?(?:Z|[+-](?:0\d|1[0-5]):[0-5]\d)$/.test(value) && validDate(value.slice(0, 10)) && Number.isFinite(Date.parse(value));
}

export function isValidMirrorTaskPatch(raw: unknown): raw is Record<string, unknown> {
  const patch = object(raw);
  if (!patch || !Object.keys(patch).length || !onlyKeys(patch, PATCH_FIELDS)) return false;
  if ('title' in patch && (typeof patch.title !== 'string' || !patch.title.trim() || patch.title.length > 1000 || patch.title.includes('\u0000'))) return false;
  if ('priority' in patch && (typeof patch.priority !== 'string' || !['Critical', 'Important', 'Normal'].includes(patch.priority))) return false;
  if ('due_date' in patch && !validDate(patch.due_date)) return false;
  if ('time_label' in patch && patch.time_label !== null && (typeof patch.time_label !== 'string' || patch.time_label.length > 100 || patch.time_label.includes('\u0000'))) return false;
  return !('done' in patch) || typeof patch.done === 'boolean';
}

export function validateMirrorTaskUpdateOutcome(raw: unknown, expected: { userId: string; taskId: string; actionId: string }): Record<string, unknown> | null {
  const outcome = object(raw);
  if (!outcome || typeof outcome.ok !== 'boolean' || outcome.action_id !== expected.actionId || outcome.scope !== 'local' || outcome.operation !== 'update_vyv_task') return null;
  if (!outcome.ok) return typeof outcome.error === 'string' && outcome.error ? outcome : null;
  const task = taskData(outcome.task);
  if (!task || task.id !== expected.taskId || task.user_id !== expected.userId || !isLocalMirrorTaskSource(task.source) || outcome.evidence !== 'database_readback') return null;
  return { ...outcome, task };
}

function patchKey(patch: Record<string, unknown>): string {
  return JSON.stringify(PATCH_FIELDS.filter(key => key in patch).map(key => [key, patch[key]]));
}

/** Stable receipt ID across network retries, distinct by owner/turn/tool call. */
export async function mirrorTaskActionId(userId: string, turnId: string, toolUseId: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(JSON.stringify(['vyv-task-action-v1', userId, turnId, toolUseId])));
  const bytes = new Uint8Array(digest).slice(0, 16);
  bytes[6] = (bytes[6] & 15) | 80;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, value => value.toString(16).padStart(2, '0')).join('');
  return hex.slice(0, 8) + '-' + hex.slice(8, 12) + '-' + hex.slice(12, 16) + '-' + hex.slice(16, 20) + '-' + hex.slice(20);
}

export function createMirrorTaskExecutor({ sb, userId, turnId, channel }: MirrorTaskExecutorOptions) {
  const observed = new Map<string, Record<string, unknown>>();
  const attempts = new Map<string, { taskId: string; expected: Record<string, unknown>; patch: Record<string, unknown> }>();
  const results: Record<string, unknown>[] = [];
  const fail = (operation: string, error: string) => ({ ok: false, operation, scope: 'local', error });
  const execute = async (name: string, rawInput: unknown, toolUseId: string): Promise<Record<string, unknown>> => {
    const operation = name;
    const finish = (outcome: Record<string, unknown>) => {
      results.push({ ...outcome, tool: name, tool_use_id: toolUseId, channel });
      return outcome;
    };
    if (!isMirrorTaskTool(name)) return finish(fail(name, 'unsupported_operation'));
    if (!UUID.test(userId) || !turnId || !toolUseId || turnId.length > 200 || toolUseId.length > 200) return finish(fail(operation, 'invalid_context'));
    const input = object(rawInput);
    if (!input) return finish(fail(operation, 'invalid_request'));
    try {
      if (name === 'search_vyv_tasks') {
        if (!onlyKeys(input, ['query', 'status', 'limit', 'cursor'])) return finish(fail(operation, 'invalid_request'));
        const query = input.query === undefined ? '' : input.query, status = input.status === undefined ? 'open' : input.status, limit = input.limit === undefined ? 10 : input.limit, offset = input.cursor === undefined ? 0 : input.cursor;
        if (typeof query !== 'string' || query.length > 200 || query.includes('\u0000') || typeof status !== 'string' || !['open', 'completed', 'all'].includes(status) || !Number.isInteger(limit) || Number(limit) < 1 || Number(limit) > 20 || !Number.isInteger(offset) || Number(offset) < 0 || Number(offset) > MAX_OFFSET) return finish(fail(operation, 'invalid_request'));
        let request = sb.from('tasks').select(MIRROR_TASK_SELECT_FIELDS).eq('user_id', userId);
        if (status !== 'all') request = request.eq('done', status === 'completed');
        // Literal substring search: user wildcards do not broaden the query.
        if (query.trim()) request = request.ilike('title', '%' + query.trim().replace(/[\\%_]/g, '\\$&') + '%');
        const response = await request.order('created_at', { ascending: false, nullsFirst: false }).order('id', { ascending: true }).range(Number(offset), Number(offset) + Number(limit));
        if (response.error || !Array.isArray(response.data)) return finish(fail(operation, 'read_failed'));
        const tasks = response.data.map((row: unknown) => buildMirrorTaskSnapshot(row, userId));
        if (tasks.some((task: Record<string, unknown> | null) => !task)) return finish(fail(operation, 'read_unconfirmed'));
        const hasMore = tasks.length > Number(limit), page = tasks.slice(0, Number(limit)) as Record<string, unknown>[];
        for (const task of page) observed.set(String(task.id), task);
        const next = Number(offset) + Number(limit);
        return finish({ ok: true, operation, scope: 'local', tasks: page, pagination: { offset, limit, has_more: hasMore, next_cursor: hasMore && next <= MAX_OFFSET ? next : null, truncated: hasMore && next > MAX_OFFSET }, evidence: 'database_readback' });
      }
      if (!onlyKeys(input, name === 'get_vyv_task' ? ['id'] : ['id', 'patch']) || typeof input.id !== 'string' || !UUID.test(input.id)) return finish(fail(operation, 'invalid_request'));
      const taskId = input.id.toLowerCase();
      if (name === 'get_vyv_task') {
        const response = await sb.from('tasks').select(MIRROR_TASK_SELECT_FIELDS).eq('user_id', userId).eq('id', taskId).maybeSingle();
        if (response.error) return finish(fail(operation, 'read_failed'));
        if (!response.data) return finish(fail(operation, 'not_found'));
        const task = buildMirrorTaskSnapshot(response.data, userId);
        if (!task) return finish(fail(operation, 'read_unconfirmed'));
        observed.set(taskId, task);
        return finish({ ok: true, operation, scope: 'local', task, evidence: 'database_readback' });
      }
      if (!isValidMirrorTaskPatch(input.patch)) return finish(fail(operation, 'invalid_patch'));
      const patch = { ...input.patch };
      if (typeof patch.due_date === 'string') {
        if (!/^\d{4}-\d{2}-\d{2}$/.test(patch.due_date)) return finish(fail(operation, 'invalid_patch'));
        // due_date is a civil date in this tool, as in the existing TaskModal.
        // Preserve that date in Tasks' due_date.slice(0,10) contract.
        patch.due_date += 'T00:00:00.000Z';
      }
      const actionId = await mirrorTaskActionId(userId, turnId, toolUseId);
      const earlier = attempts.get(actionId);
      if (earlier && (earlier.taskId !== taskId || patchKey(earlier.patch) !== patchKey(patch))) return finish({ ...fail(operation, 'idempotency_conflict'), action_id: actionId });
      const expected = earlier?.expected ?? observed.get(taskId);
      if (!expected) return finish(fail(operation, 'target_not_observed'));
      if (!isLocalMirrorTaskSource(expected.source)) return finish(fail(operation, 'provider_route_required'));
      if (!earlier) attempts.set(actionId, { taskId, expected, patch });
      const response = await sb.rpc('mirror_update_task', { p_user_id: userId, p_action_id: actionId, p_task_id: taskId, p_expected: expected, p_patch: patch });
      if (response.error) return finish({ ...fail(operation, 'execution_failed'), action_id: actionId });
      const outcome = validateMirrorTaskUpdateOutcome(response.data, { userId, taskId, actionId });
      if (!outcome) return finish({ ...fail(operation, 'write_unconfirmed'), action_id: actionId });
      if (outcome.ok) observed.set(taskId, outcome.task as Record<string, unknown>);
      return finish(outcome);
    } catch {
      return finish(fail(operation, 'execution_failed'));
    }
  };
  return { execute, results };
}
